import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
  Tooltip
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InfoOutlineIcon
} from '@chakra-ui/icons';
import { codeBenchActions } from '@codametrix/ui-common';
import { QueueRow } from '../queue-row/queue-row';
import { GroupMetricCard } from '../group-metric-card/group-metric-card';
import {
  fetchAssignedQueues,
  fetchTickerStats,
  claimNextCase,
  ClaimNextCaseResult
} from '../../actions/coder-view.action';
import { launchWorkitemInAmplify } from '../../actions/cases.action';
import { CoderViewState } from '../../reducers/coder-view.reducer';
import './coder-top-section.scss';

const selectCoderView = (state: any): CoderViewState => state.coderView;

// Fallback used only when the mutation itself failed at the transport/GraphQL
// level (no backend payload to read a message from) — the server owns every
// other message's text and kind (see ClaimNextCaseResult.message / .messageKind).
const CLAIM_FAILURE_MESSAGE = 'Failed to start coding. Please try again.';

type ClaimMessageKind = 'info' | 'error';

interface ClaimMessage {
  kind: ClaimMessageKind;
  text: string;
}

// CDMX-15947 — the coder "My Work" top section: header, the prioritized
// "My Queues" panel, and the group-metric cards. Rendered (via a portal from
// CoderViewContainer) into the worklist's header slot, so it sits in the content
// column above the tabs, right of the full-height nav.
export const CoderTopSection: React.FC = () => {
  const dispatch = useDispatch();
  const { assignedQueues, tickerStats, isFetching, errors } = useSelector(
    selectCoderView
  );
  const service = useSelector(
    (state: any) => state.ui?.serviceLine?.name as string | undefined
  );
  // Drives the '/v2' path suffix. The bench layout is selected by a
  // `window.location.pathname.endsWith('/v2/')` sniff (Utils.doesPathEndWithV2),
  // so omitting it silently renders the legacy bench inside the nav gutter.
  const hasAmplifyCaseRunV2Access = useSelector(
    (state: any) => state.ui?.hasAmplifyCaseRunV2Access as boolean | undefined
  );

  const queueScrollRef = useRef<HTMLDivElement>(null);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [claimMessage, setClaimMessage] = useState<ClaimMessage | null>(null);

  useEffect(() => {
    if (!service) {
      return;
    }
    dispatch(fetchAssignedQueues({ service }) as any);
    dispatch(fetchTickerStats({ service }) as any);
  }, [dispatch, service]);

  // Ask the backend for a case in this queue — one the coder already owns if there is
  // one, otherwise the next unassigned one it claims for them — and open the bench on it.
  const handleStartCoding = async (queueId: string) => {
    if (!service || claimingId) {
      return;
    }
    setClaimingId(queueId);
    setClaimMessage(null);
    try {
      const action: any = await dispatch(
        claimNextCase({ collectionId: queueId, service }) as any
      );
      const result = action?.payload as ClaimNextCaseResult | undefined;
      if (action?.error || !result) {
        setClaimMessage({ kind: 'error', text: CLAIM_FAILURE_MESSAGE });
        return;
      }
      if (result.claimed && result.caseId && result.processId) {
        // Launch through the same thunk the worklist grid uses, so the URL (including
        // the '/v2' suffix) and the router state stay identical to a worklist launch.
        dispatch(
          launchWorkitemInAmplify({
            serviceLine: result.service ?? service ?? '',
            caseId: result.caseId,
            processId: result.processId,
            hasAmplifyCaseRunV2Access: !!hasAmplifyCaseRunV2Access,
            // CDMX-15442 — no cursor context, so the bench opens without worklist
            // Next/Prev. Start Coding hands out one case at a time.
            servicedeskMode: false,
            worklistContext: {},
            workitemId: ''
          }) as any
        );
        if (hasAmplifyCaseRunV2Access) {
          dispatch(codeBenchActions.hideSideNavBar());
        }
      } else {
        setClaimMessage({
          kind: result.messageKind === 'info' ? 'info' : 'error',
          text: result.message ?? CLAIM_FAILURE_MESSAGE
        });
      }
    } finally {
      setClaimingId(null);
    }
  };

  const scrollQueues = (direction: 1 | -1) => {
    queueScrollRef.current?.scrollBy({
      top: direction * 160,
      behavior: 'smooth'
    });
  };

  const metricCards = useMemo(
    () => [
      {
        title: 'Cases on Hold',
        value: tickerStats.holdByCoder + tickerStats.providerHolds,
        subtitle: 'Pending research or documentation'
      },
      {
        title: 'Escalated Cases',
        value: tickerStats.managerHolds,
        subtitle: 'Awaiting manager review'
      },
      {
        title: 'Completed Today',
        value: tickerStats.submitted,
        subtitle: 'Cases finished today'
      }
    ],
    [tickerStats]
  );

  const queuesLoading =
    isFetching.assignedQueues && assignedQueues.length === 0;

  return (
    <Box className="cmx-coder-top" data-testid="coder-top-section">
      <Flex className="cmx-coder-top__header" align="center">
        <Heading className="cmx-coder-top__title" size="md">
          My Work
        </Heading>
      </Flex>

      <div className="cmx-coder-top__grid">
        <div className="cmx-coder-top__queue-card" data-testid="coder-top-queues">
          <Flex
            className="cmx-coder-top__queue-header"
            justify="space-between"
            align="flex-start">
            <Box>
              <Text className="cmx-coder-top__queue-title">My Queues</Text>
              <Text className="cmx-coder-top__queue-subtitle">
                Your assigned case queues
              </Text>
            </Box>
            <Tooltip
              label="Queues assigned to you, ordered by priority. Start Coding opens the worklist for that queue."
              hasArrow
              placement="left">
              <InfoOutlineIcon
                className="cmx-coder-top__queue-info"
                data-testid="coder-top-queues-info"
                aria-label="About my queues"
              />
            </Tooltip>
          </Flex>

          {errors.assignedQueues && (
            <Text
              className="cmx-coder-top__error"
              data-testid="coder-top-queues-error">
              {errors.assignedQueues}
            </Text>
          )}

          {queuesLoading ? (
            <Flex justify="center" align="center" minH="120px">
              <Spinner size="md" data-testid="coder-top-queues-loading" />
            </Flex>
          ) : (
            <div
              className="cmx-coder-top__queue-scroll"
              ref={queueScrollRef}
              data-testid="coder-top-queue-scroll">
              {assignedQueues.length === 0 ? (
                <Text
                  className="cmx-coder-top__empty"
                  data-testid="coder-top-queues-empty">
                  No queues to display
                </Text>
              ) : (
                assignedQueues.map(queue => (
                  <QueueRow
                    key={queue.id}
                    queueName={queue.queueName}
                    totalInQueue={queue.totalInQueue}
                    criticalAge={queue.criticalAge}
                    onStartCoding={() => handleStartCoding(queue.id)}
                    startCodingDisabled={!!claimingId}
                    className="cmx-coder-top__queue-row"
                  />
                ))
              )}
            </div>
          )}

          {claimMessage && (
            <Text
              className={
                claimMessage.kind === 'info'
                  ? 'cmx-coder-top__info'
                  : 'cmx-coder-top__error'
              }
              data-testid="coder-top-claim-message">
              {claimMessage.text}
            </Text>
          )}

          {assignedQueues.length > 4 && (
            <Flex className="cmx-coder-top__queue-carousel" justify="flex-end" gap={1}>
              <IconButton
                aria-label="Scroll queues up"
                icon={<ChevronUpIcon />}
                size="xs"
                variant="ghost"
                onClick={() => scrollQueues(-1)}
              />
              <IconButton
                aria-label="Scroll queues down"
                icon={<ChevronDownIcon />}
                size="xs"
                variant="ghost"
                onClick={() => scrollQueues(1)}
              />
            </Flex>
          )}
        </div>

        <div
          className="cmx-coder-top__sidebar"
          data-testid="coder-top-metric-cards">
          {errors.tickerStats && (
            <Text
              className="cmx-coder-top__error"
              data-testid="coder-top-metrics-error">
              {errors.tickerStats}
            </Text>
          )}
          {metricCards.map(card => (
            <GroupMetricCard
              key={card.title}
              title={card.title}
              value={card.value}
              subtitle={card.subtitle}
              className="cmx-coder-top__metric-card"
            />
          ))}
        </div>
      </div>
    </Box>
  );
};

CoderTopSection.displayName = 'CoderTopSection';
export default CoderTopSection;
